import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Users, UserPlus, Ban, Flag, X, MoreHorizontal, Copy } from "lucide-react";
import { toast } from "sonner";
import { io } from "socket.io-client";
import { api, useAuth } from "../contexts/AuthContext";
import AvatarDisplay from "../components/AvatarDisplay";
import ReportModal from "../components/ReportModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(ts) {
  try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function SystemMessage({ content }) {
  return (
    <div className="flex justify-center my-2">
      <span className="text-xs text-slate-500 bg-[#141322] px-3 py-1 rounded-full">{content}</span>
    </div>
  );
}

// ── Safe in-room user card (no profile page, no persistent URL) ───────────────
function UserCard({ userId, alias, avatarId, onClose, onAction }) {
  const [memberSince, setMemberSince] = useState(null);

  useEffect(() => {
    api.get(`/users/${userId}`).then(r => {
      if (r.data.created_at) {
        const days = Math.floor((Date.now() - new Date(r.data.created_at)) / 86400000);
        if (days < 1)       setMemberSince("Joined today");
        else if (days < 7)  setMemberSince(`Joined ${days}d ago`);
        else if (days < 30) setMemberSince(`Joined ${Math.floor(days / 7)}w ago`);
        else                setMemberSince(`Member for ${Math.floor(days / 30)}mo`);
      }
    }).catch(() => {});
  }, [userId]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 10 }}
        transition={{ duration: 0.18 }}
        className="relative bg-[#0E0E17] border border-purple-500/30 rounded-2xl p-6 w-full max-w-xs shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-600 hover:text-slate-300 transition-colors">
          <X className="w-4 h-4" />
        </button>

        {/* Avatar + name */}
        <div className="flex flex-col items-center mb-5">
          <AvatarDisplay avatarId={avatarId} size="xl" status="online" showStatus />
          <h3 className="text-xl font-black text-white mt-3 font-[Outfit]">{alias}</h3>
          {memberSince && <p className="text-xs text-slate-500 mt-1">{memberSince}</p>}
          <div className="flex items-center gap-1.5 mt-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs text-emerald-400">Online now</span>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <button
            data-testid="usercard-add-friend"
            onClick={() => onAction("friend")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-purple-500/15 border border-purple-500/25 text-white font-medium text-sm hover:bg-purple-500/25 transition-colors"
          >
            <UserPlus className="w-4 h-4 text-purple-400" /> Add Friend
          </button>
          <div className="flex gap-2">
            <button
              data-testid="usercard-block"
              onClick={() => onAction("block")}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#141322] border border-slate-700 text-slate-400 text-sm hover:text-amber-400 hover:border-amber-500/40 transition-colors"
            >
              <Ban className="w-4 h-4" /> Block
            </button>
            <button
              data-testid="usercard-report"
              onClick={() => onAction("report")}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#141322] border border-slate-700 text-slate-400 text-sm hover:text-red-400 hover:border-red-500/40 transition-colors"
            >
              <Flag className="w-4 h-4" /> Report
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

const EMOJI_REACTIONS = ["👍", "😂", "❤️", "🔥"];

function ChatMessage({ msg, currentUserId, onAvatarClick, onReact, onReportMessage }) {
  const [showDotMenu, setShowDotMenu] = useState(false);
  const isOwn = msg.user_id === currentUserId;
  const reactions = msg.reactions || {};
  const hasAnyReaction = EMOJI_REACTIONS.some(e => (reactions[e] || []).length > 0);

  const copyMsg = () => { try { navigator.clipboard.writeText(msg.content); } catch {} setShowDotMenu(false); };

  return (
    <div className={`group flex gap-3 msg-enter ${isOwn ? "flex-row-reverse" : ""}`}>
      <div className={`shrink-0 mt-1 ${!isOwn ? "cursor-pointer" : ""}`}
        onClick={() => !isOwn && onAvatarClick(msg)} title={!isOwn ? `View ${msg.alias}` : undefined}>
        <AvatarDisplay avatarId={msg.avatar_id} size="sm" />
      </div>

      <div className={`max-w-[75%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
        <div className={`flex items-baseline gap-2 mb-1 ${isOwn ? "flex-row-reverse" : ""}`}>
          <span className={`text-xs font-semibold text-slate-300 ${!isOwn ? "cursor-pointer hover:text-purple-300 transition-colors" : ""}`}
            onClick={() => !isOwn && onAvatarClick(msg)}>{msg.alias}</span>
          <span className="text-[10px] text-slate-600">{formatTime(msg.timestamp)}</span>
        </div>

        {/* Bubble + three-dot */}
        <div className={`flex items-start gap-1 ${isOwn ? "flex-row-reverse" : ""}`}>
          <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
            isOwn ? "bg-gradient-to-r from-violet-600/80 to-purple-600/80 text-white rounded-tr-sm"
                  : "bg-[#141322] text-slate-200 border border-purple-500/10 rounded-tl-sm"
          }`}>{msg.content}</div>

          {/* ··· menu */}
          <div className="relative self-center">
            <button data-testid={`msg-menu-${msg.msg_id}`} onClick={() => setShowDotMenu(v => !v)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-slate-500 hover:text-slate-300 p-0.5 mt-1">
              <MoreHorizontal className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {showDotMenu && (
                <motion.div initial={{ opacity: 0, scale: 0.95, y: -4 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className={`absolute ${isOwn ? "right-0" : "left-0"} top-7 z-50 bg-[#1C1936] border border-purple-500/30 rounded-xl p-1 min-w-[160px] shadow-2xl`}
                  onClick={e => e.stopPropagation()}>
                  <button onClick={copyMsg}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                    <Copy className="w-3.5 h-3.5" /> Copy Message
                  </button>
                  {!isOwn && (
                    <button data-testid={`report-msg-${msg.msg_id}`}
                      onClick={() => { onReportMessage(msg); setShowDotMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                      <Flag className="w-3.5 h-3.5" /> Report Message
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Emoji reactions */}
        <div className={`flex gap-1 mt-1.5 flex-wrap ${isOwn ? "justify-end" : ""}`}>
          {EMOJI_REACTIONS.map(emoji => {
            const reactors = reactions[emoji] || [];
            const count = reactors.length;
            const hasReacted = reactors.includes(currentUserId);
            return (
              <motion.button key={emoji} whileTap={{ scale: 0.82 }}
                onClick={() => onReact(msg.msg_id, emoji)}
                data-testid={`react-${emoji.codePointAt(0)}-${msg.msg_id}`} title={emoji}
                className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-all
                  ${hasReacted ? "bg-purple-500/25 border-purple-400/50 text-purple-100"
                  : count > 0 ? "bg-[#1C1936] border-slate-600/50 text-slate-300 hover:border-purple-500/40"
                  : `bg-transparent border-transparent text-slate-500 ${hasAnyReaction ? "opacity-60" : "opacity-0 group-hover:opacity-60"} hover:bg-[#1C1936] hover:border-slate-700 hover:opacity-100`}`}>
                <span className="leading-none">{emoji}</span>
                {count > 0 && (
                  <motion.span key={count} initial={{ scale: 1.5, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} className="font-mono font-bold leading-none">
                    {count}
                  </motion.span>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Members list with admin/mod sort ─────────────────────────────────────────
const ROLE_ORDER = { admin: 0, mod: 1, member: 2, user: 2 };

function MembersList({ members, currentUserId }) {
  const sorted = [...members].sort((a, b) => {
    const ar = ROLE_ORDER[a.role] ?? 2;
    const br = ROLE_ORDER[b.role] ?? 2;
    if (ar !== br) return ar - br;
    return (a.alias || "").localeCompare(b.alias || "");
  });

  return (
    <div className="p-3 space-y-1">
      <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 px-2 mb-2">
        In This Room · {members.length}
      </p>
      {sorted.length === 0 && <p className="text-xs text-slate-600 px-2">Just you so far</p>}
      {sorted.map(m => {
        const isAdmin = m.role === "admin" || m.role === "mod";
        const isYou = m.user_id === currentUserId;
        return (
          <div key={m.user_id} data-testid={`member-${m.user_id}`}
            className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#141322] transition-colors">
            <AvatarDisplay avatarId={m.avatar_id} size="sm" status={m.status} showStatus />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`text-sm truncate ${isAdmin ? "text-cyan-300 font-semibold" : "text-slate-200"}`}>
                  {m.alias}{isYou ? " (you)" : ""}
                </span>
                {isAdmin && (
                  <span className="text-[9px] font-mono bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                    MOD
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ChatroomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [input, setInput] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  const [reportMsgTarget, setReportMsgTarget] = useState(null); // { user_id, alias, avatar_id, msg_id, room_id, message_content }
  const [reportTarget, setReportTarget] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [notification, setNotification] = useState("");
  const [userCard, setUserCard] = useState(null); // { user_id, alias, avatar_id }
  const [isRoomOpen, setIsRoomOpen] = useState(true);   // schedule: open/closed
  const [closedMsg, setClosedMsg] = useState("");       // message when closed
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);
  const hasJoinedRef = useRef(false);  // prevents duplicate join_room on same socket

  useEffect(() => {
    api.get(`/rooms/${roomId}`).then(r => setRoom(r.data)).catch(() => navigate("/lobby"));
    api.get(`/rooms/${roomId}/messages`).then(r => setMessages(r.data)).catch(() => {});
    // Check schedule status
    api.get("/rooms/status").then(r => {
      setIsRoomOpen(r.data.is_open);
      if (!r.data.is_open) setClosedMsg(r.data.message);
    }).catch(() => {});

    socketRef.current = io(BACKEND_URL, { path: "/api/socket.io", withCredentials: true, transports: ["websocket", "polling"] });

    // Guard: emit join_room only once per socket instance.
    // Using a ref (not state) ensures it survives React StrictMode's double-invoke
    // without blocking legitimate reconnection joins.
    hasJoinedRef.current = false;

    socketRef.current.on("connect", () => {
      if (!hasJoinedRef.current) {
        hasJoinedRef.current = true;
        socketRef.current.emit("join_room", { room_id: roomId });
      }
    });

    socketRef.current.on("room_members", ({ members: m }) => setMembers(m));
    socketRef.current.on("new_message", (msg) => setMessages(prev => [...prev, msg]));
    socketRef.current.on("user_joined_room", ({ alias }) => {
      setMembers(prev => [...prev.filter(m => m.alias !== alias)]);
    });
    socketRef.current.on("user_left_room", ({ user_id }) => {
      setMembers(prev => prev.filter(m => m.user_id !== user_id));
    });
    socketRef.current.on("system_message", ({ content }) => {
      setMessages(prev => [...prev, { msg_id: `sys_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, is_system: true, content, timestamp: new Date().toISOString() }]);
    });
    socketRef.current.on("user_typing", ({ alias, is_typing }) => {
      setTypingUsers(prev => is_typing ? [...new Set([...prev, alias])] : prev.filter(a => a !== alias));
    });
    socketRef.current.on("message_deleted", ({ msg_id }) => {
      setMessages(prev => prev.map(m => m.msg_id === msg_id ? { ...m, content: "[Message removed by moderator]" } : m));
    });
    socketRef.current.on("reaction_update", ({ msg_id, reactions }) => {
      setMessages(prev => prev.map(m => m.msg_id === msg_id ? { ...m, reactions } : m));
    });
    socketRef.current.on("muted_notification", ({ message }) => {
      setNotification(message);
      setTimeout(() => setNotification(""), 5000);
    });
    socketRef.current.on("account_moderated", ({ message }) => {
      setNotification(message);
    });
    socketRef.current.on("room_closed_error", ({ message }) => {
      setNotification(message);
      setTimeout(() => setNotification(""), 5000);
    });
    // Schedule events
    socketRef.current.on("rooms_closed", ({ message }) => {
      setIsRoomOpen(false);
      setClosedMsg(message || "Who's On? is closed for tonight. Back tomorrow at 7pm.");
      setMessages([]);  // clear messages (they're being wiped on the server)
    });
    socketRef.current.on("rooms_opened", ({ message }) => {
      setIsRoomOpen(true);
      setClosedMsg("");
      setNotification(message || "Who's On? is now open — chat until 11pm!");
      setTimeout(() => setNotification(""), 5000);
    });
    socketRef.current.on("room_status_update", ({ is_open, message }) => {
      setIsRoomOpen(is_open);
      if (!is_open) setClosedMsg(message || "");
    });
    socketRef.current.on("room_warning", ({ message }) => {
      setNotification(message);
      setTimeout(() => setNotification(""), 60000);
    });
    socketRef.current.on("new_friend_request", ({ from_alias }) => {
      toast(`${from_alias} wants to be friends`, {
        description: "Tap to view your friend requests",
        action: { label: "View", onClick: () => navigate("/friends") },
        duration: 8000,
        icon: "👋",
      });
    });
    // Admin: real-time auto-moderation alert
    socketRef.current.on("auto_flag_alert", ({ alias, reason, auto_deleted }) => {
      toast.warning(`🤖 Auto-flagged: ${alias}`, {
        description: `${reason}${auto_deleted ? " · Message auto-deleted" : " · Review needed"}`,
        action: { label: "Review", onClick: () => navigate("/admin") },
        duration: 15000,
      });
    });

    return () => {
      hasJoinedRef.current = false;
      if (socketRef.current?.connected) {
        socketRef.current.emit("leave_room", { room_id: roomId });
        socketRef.current.disconnect();
      }
    };
  }, [roomId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || !socketRef.current?.connected) return;
    socketRef.current.emit("send_message", { room_id: roomId, content: input.trim() });
    setInput("");
    socketRef.current.emit("typing", { room_id: roomId, is_typing: false });
  };

  const handleInputChange = (val) => {
    setInput(val);
    if (socketRef.current?.connected) {
      socketRef.current.emit("typing", { room_id: roomId, is_typing: val.length > 0 });
      clearTimeout(typingTimer.current);
      if (val.length > 0) {
        typingTimer.current = setTimeout(() => {
          socketRef.current?.emit("typing", { room_id: roomId, is_typing: false });
        }, 2000);
      }
    }
  };

  const handleReact = (msgId, emoji) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("react_message", { room_id: roomId, msg_id: msgId, emoji });
  };

  const handleAvatarClick = (msg) => {
    setUserCard({ user_id: msg.user_id, alias: msg.alias, avatar_id: msg.avatar_id });
  };

  const handleReportMessage = (msg) => {
    setReportMsgTarget({
      user_id: msg.user_id,
      alias: msg.alias,
      avatar_id: msg.avatar_id,
      msg_id: msg.msg_id,
      room_id: roomId,
      message_content: msg.content
    });
  };

  const handleUserCardAction = async (action) => {
    if (!userCard) return;
    if (action === "friend") {
      try { await api.post(`/friends/request/${userCard.user_id}`); setNotification(`Friend request sent to ${userCard.alias}`); }
      catch (e) { setNotification(e.response?.data?.detail || "Could not send request"); }
      setTimeout(() => setNotification(""), 3000);
    } else if (action === "block") {
      try { await api.post(`/users/block/${userCard.user_id}`); setNotification(`${userCard.alias} blocked`); }
      catch { setNotification("Could not block user"); }
      setTimeout(() => setNotification(""), 3000);
    } else if (action === "report") {
      setReportTarget({ user_id: userCard.user_id, alias: userCard.alias, msg_id: null });
    }
    setUserCard(null);
  };

  const submitReport = async (target, reason) => {
    try {
      await api.post("/reports", { reported_user_id: target.user_id, msg_id: target.msg_id, reason });
      setNotification("Report submitted. Thank you.");
    } catch { setNotification("Failed to submit report"); }
    setReportTarget(null);
    setTimeout(() => setNotification(""), 3000);
  };

  return (
    <div className="h-screen bg-[#07070B] flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-14 bg-[#0E0E17]/90 backdrop-blur-xl border-b border-purple-500/20 flex items-center px-4 gap-3 shrink-0">
        <button data-testid="back-to-lobby-btn" onClick={() => navigate("/lobby")} className="text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-white font-[Outfit] truncate">{room?.name || "Loading..."}</h1>
          <p className="text-xs text-slate-400 truncate">{room?.description}</p>
        </div>
        <button
          data-testid="toggle-members-btn"
          onClick={() => setShowSidebar(v => !v)}
          className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
        >
          <Users className="w-5 h-5" />
          <span className="text-xs">{members.length}</span>
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ── LEFT user list — always visible on desktop, overlay on mobile ── */}
        {/* Desktop */}
        <aside className="hidden md:flex flex-col w-52 shrink-0 bg-[#0E0E17] border-r border-purple-500/15 overflow-y-auto">
          <MembersList members={members} currentUserId={user?.user_id} />
        </aside>

        {/* Mobile overlay */}
        <AnimatePresence>
          {showSidebar && (
            <>
              <div className="md:hidden fixed inset-0 bg-black/40 z-10" onClick={() => setShowSidebar(false)} />
              <motion.div
                initial={{ x: -208 }} animate={{ x: 0 }} exit={{ x: -208 }}
                transition={{ type: "spring", damping: 28, stiffness: 320 }}
                className="md:hidden absolute left-0 top-0 bottom-0 z-20 w-52 bg-[#0E0E17] border-r border-purple-500/15 overflow-y-auto"
              >
                <div className="flex items-center justify-between p-4 border-b border-purple-500/10">
                  <span className="text-sm font-semibold text-white font-[Outfit]">In This Room</span>
                  <button onClick={() => setShowSidebar(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <MembersList members={members} currentUserId={user?.user_id} />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ── Messages + input ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin" data-testid="messages-container">
            {messages.map(msg =>
              msg.is_system
                ? <SystemMessage key={msg.msg_id} content={msg.content} />
                : <ChatMessage key={msg.msg_id} msg={msg} currentUserId={user?.user_id} onAvatarClick={handleAvatarClick} onReact={handleReact} onReportMessage={handleReportMessage} />
            )}
            {typingUsers.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-500 pl-2">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <span key={i} className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
                {typingUsers.join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Notification toast */}
          <AnimatePresence>
            {notification && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                className="mx-4 mb-2 p-2.5 bg-[#141322] border border-purple-500/30 rounded-xl text-sm text-slate-300 text-center">
                {notification}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input */}
          <div className="p-3 bg-[#0B0B12] border-t border-purple-500/15">
            {isRoomOpen ? (
              <form onSubmit={sendMessage} className="flex gap-2">
                <AvatarDisplay avatarId={user?.avatar_id} size="sm" className="mt-0.5 shrink-0" />
                <input
                  data-testid="message-input"
                  value={input}
                  onChange={e => handleInputChange(e.target.value)}
                  placeholder="Say something..."
                  className="flex-1 bg-[#141322] border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors"
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) sendMessage(e); }}
                />
                <button
                  data-testid="send-message-btn"
                  type="submit"
                  disabled={!input.trim()}
                  className="w-10 h-10 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-500 flex items-center justify-center text-white disabled:opacity-40 hover:opacity-90 active:scale-95 transition-all"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <div data-testid="room-closed-notice" className="text-center py-3 px-4">
                <p className="text-slate-400 text-sm leading-relaxed">
                  🔒 {closedMsg || "Who's On? is closed for tonight. Back tomorrow at 7pm."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message report modal */}
      <AnimatePresence>
        {reportMsgTarget && (
          <ReportModal type="message" target={reportMsgTarget} onClose={() => setReportMsgTarget(null)} />
        )}
      </AnimatePresence>

      {/* User card report */}
      {reportTarget && (
        <ReportModal type="user" target={{ user_id: reportTarget.user_id, alias: reportTarget.alias, avatar_id: reportTarget.avatar_id, room_id: roomId }}
          onClose={() => setReportTarget(null)} />
      )}

      {/* Safe in-room user card — no profile page, no persistent URL */}
      <AnimatePresence>
        {userCard && (
          <UserCard
            userId={userCard.user_id}
            alias={userCard.alias}
            avatarId={userCard.avatar_id}
            onClose={() => setUserCard(null)}
            onAction={handleUserCardAction}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
